import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import CardHeader from '../../Util/CardHeader';
import { Privilege } from '../../../api';
import useSession from '../../Util/useSession';
import labelTasksService from '../../../services/labelTasks';
import useMe from '../../Util/useMe';
import { useSnackbar } from 'notistack';
import { PagingState, SortingState, Sorting, IntegratedPaging, IntegratedSorting } from '@devexpress/dx-react-grid';
import { Grid, Table, TableHeaderRow, TableColumnResizing, PagingPanel } from '@devexpress/dx-react-grid-material-ui';
import useLocalStorage from '../../Util/useLocalStorage';
import styled from 'styled-components';
import { CheckCircle, HelpOutline } from '@material-ui/icons';
import { CircularProgressWithLabel } from '../SessionUploadButton';
import LabelTasksFilter from './LabelTasksFilter';
import { initialState, reducer, TRow, UserFragment, RunData, EvictionStatus } from './sessionLabelOverviewReducer';
import SessionLabelOwner from './SessionLabelOwner';
import useHasPrivilege from '../../Util/useHasPrivilege';
import useValidationFlagsPerTask, { TaskFlags } from './useValidationFlagsPerTask';
import FlagIcon from '@material-ui/icons/Flag';
import { FlagStatus } from './useValidationFlags';
import SessionRunEvict from '../SessionLabelEvict';
import useInterval from '../../Util/useInterval';
import { fetchGet } from '../../Util/fetchAPI';
import getLabelTasks from './LabelTask.js';
import { useSessionLineup } from './useLabelData';
import { Button } from '@material-ui/core';

type TParams = {
  session: string;
};

interface Props extends RouteComponentProps<TParams> {}

const sortingStateColumnExtensions = [{ columnName: 'progress', sortingEnabled: false }];

const SessionLabelOverview = (props: Props) => {

  const sessionId: string = props.match.params.session;
  const [state, dispatch] = useReducer(reducer, initialState);

  const { enqueueSnackbar } = useSnackbar();
  const { session, refetch: refetchSession } = useSession(sessionId);

  useEffect(() => {
    dispatch({ type: 'session-loaded', payload: session || undefined });
  }, [session]);

  const loadLabelTasks = useCallback(async () => {
    try {
      const result = await labelTasksService.getLabelTasks(sessionId);
      dispatch({ type: 'label-tasks-loaded', payload: result?.items ?? [] });
    } catch (error) {
      enqueueSnackbar('Error retrieving label tasks', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [sessionId, enqueueSnackbar]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchSession(), loadLabelTasks()]);
  }, [refetchSession, loadLabelTasks]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useInterval(refetch, 60000);

  const isAdmin = useHasPrivilege(Privilege.Admin);
  const lineup = useSessionLineup(sessionId);

  let history = useHistory();
  const { data: me } = useMe({
    onCompleted: (data) => {
      dispatch({ type: 'set-me', payload: data.me?.id });
    },
  });
  const canAssignTasksToAnyone = useHasPrivilege(Privilege.AssignLabelTasks);

  const [sorting, setSorting] = useLocalStorage<Sorting[]>('labelTasks-sort', [{ columnName: 'id', direction: 'asc' }]);

  const runId: string | undefined = state.session?.lastRun?.id || undefined;
  const { flags } = useValidationFlagsPerTask(runId);

  const openTask = useCallback(
    (row: TRow) => {
      if (canAssignTasksToAnyone || (row.owner && String(row.owner.id) === me?.me?.id))
        history.push('/session/label/' + props.match.params.session + '/task/' + row.id);
    },
    [history, props.match.params.session, canAssignTasksToAnyone, me]
  );
  
  const reloadStatus = useCallback(() => {
    const runId: string = state.session?.lastRun?.id || '';
    if (runId === '') return;

    fetchGet('/gateway/label-assistant-v2/api/v1/label/run/' + runId + '/status')
      .then((returnData: RunData) => {
        dispatch({ type: 'task-data-loaded', payload: returnData });
        // console.log('Last labeled tasks frame ids:', returnData);
      })
      .catch((error) => {
        console.log('Error when retrieving tasks data:', error);
        enqueueSnackbar('Error retrieving labeltask progress', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      });
  }, [enqueueSnackbar, state.session]);
  
  useEffect(() => {
    reloadStatus();
  }, [reloadStatus]);
  
  useInterval(() => {
    reloadStatus();
  }, 10000);

  const columns = useMemo(() => {
    return [
      {
        name: 'progress',
        title: 'Progress',
        getCellValue: (row: TRow) =>
          row.progress === 100 ? (
            <CheckCircle color="secondary" />
          ) : (
            <CircularProgressWithLabel color="secondary" value={row.progress} />
          ),
        prio: 1,
      },
      { name: 'id', title: 'ID', prio: 1 },
      { name: 'period', title: 'Period', prio: 3 },
      { name: 'startTime', title: 'Start', prio: 5 },
      { name: 'endTime', title: 'End', prio: 1 },
      { name: 'team', title: 'Team', prio: 1 },
      { name: 'type', title: 'Type', prio: 1 },
      { 
        name: 'identity', 
        title: 'Identity',
        getCellValue: (row: TRow) => {
          return (
            row.identity !== "" ? row.identity : <HelpOutline htmlColor="#900" />
          );
        }, 
        prio: 1
      },
      {
        name: 'owner',
        title: 'Assigned',
        getCellValue: (row: TRow) => {
          return (
            <SessionLabelOwner
              session={state.session}
              value={row}
              onChange={(user: UserFragment | null) => {
                dispatch({
                  type: 'set-task-owner',
                  payload: {
                    owner: user,
                    task: row.id,
                  },
                });
                labelTasksService
                  .changeOwner(row.tlt.id, user?.id ?? null)
                  .then(loadLabelTasks)
                  .catch(() => enqueueSnackbar('Failed to change task owner', { variant: 'error', autoHideDuration: 3000 }));
              }}
            />
          );
        },
        prio: 1,
      },
      { 
        name: 'flags', 
        title: 'Flags', 
        getCellValue: (row: TRow) => {
          const taskFlags: TaskFlags | undefined = flags.find(f => String(f.task_id) === row.id);
          const unresolvedFlagsCount: number = taskFlags ? taskFlags.flags.filter(f => f.status === FlagStatus.Unresolved).length : 0;
          const allowedFlagsCount: number = taskFlags ? taskFlags.flags.filter(f => f.status === FlagStatus.Allowed).length : 0;
          return (
            <>
              { unresolvedFlagsCount > 0 && <><FlagIcon htmlColor="#900" /> <b>{unresolvedFlagsCount}</b></> }
              &nbsp;&nbsp;
              { allowedFlagsCount > 0 && <><FlagIcon htmlColor="#090" /> <b>{allowedFlagsCount}</b></> }
            </>
          );
        },
        prio: 1,
      },
    ];
  }, [flags, state.session, enqueueSnackbar, loadLabelTasks]);

  const addMissingLabelTasks = useCallback(async () => {
    if (!state.session) return;
    const inputList = getLabelTasks(state.session, lineup, true);

    await labelTasksService.clearLabelTasks(sessionId);
    await labelTasksService.addLabelTasks(sessionId, inputList);
    await refetch();
  }, [state.session, lineup, sessionId, refetch]);

  if(state.runData?.eviction.status === EvictionStatus.Available) {
    return (
      <div className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5" style={{ overflow: 'visible' }}>
        <TitleWrapper>
          <CardHeader>Label tasks 
          </CardHeader>
        </TitleWrapper>
        <Body>
          <FilterContainer>

            {isAdmin && <Button variant="contained" style={{backgroundColor: "rgb(153, 0, 0)", color: "#fff", }} onClick={addMissingLabelTasks}>
              Regenerate label tasks
            </Button>}

            <LabelTasksFilter
              refetch={refetch}
              session={state.session}
              labelTasks={state.labelTasks}
              runData={state.runData}
              search={state.search}
              onSearchChange={(search) => dispatch({ type: 'set-search', payload: search })}
              types={state.types}
              onTypesChange={(types) => dispatch({ type: 'set-task-types', payload: types })}
              periods={state.periods}
              onPeriodsChange={(periods) => dispatch({ type: 'set-periods', payload: periods })}
              unfinishedOnly={state.unfinishedOnly}
              onChangeUnfinishedOnly={(unfinishedOnly) =>
                dispatch({
                  type: 'set-unfinished-only',
                  payload: unfinishedOnly,
                })
              }
              mineOnly={state.mineOnly}
              onChangeMineOnly={(mineOnly) =>
                dispatch({
                  type: 'set-mine-only',
                  payload: mineOnly,
                })
              }
              unassignedOnly={state.unassignedOnly}
              onChangeUnassignedOnly={(unassignedOnly) =>
                dispatch({
                  type: 'set-unassigned-only',
                  payload: unassignedOnly,
                })
              }
            />
          </FilterContainer>
          <TableContainer>
            <Grid rows={state.filteredRows} columns={columns}>
              <PagingState defaultCurrentPage={0} defaultPageSize={100} />
              <PagingPanel pageSizes={[10, 25, 50, 100]} />
              <IntegratedPaging />
              <SortingState
                sorting={[...sorting, { columnName: 'id', direction: 'asc' }]}
                onSortingChange={setSorting}
                columnExtensions={sortingStateColumnExtensions}
              />
              <IntegratedSorting />
              <Table
                rowComponent={(row) => (
                  <Table.Row
                    {...row}
                    hover
                    onClick={() => openTask(row.row)}
                    // eslint-disable-next-line no-alert
                  />
                )}
              />
              <TableColumnResizing
                columnWidths={state.columnWidths}
                onColumnWidthsChange={(widths) =>
                  dispatch({
                    type: 'set-column-widths',
                    payload: widths,
                  })
                }
              />
              <TableHeaderRow showSortingControls />
            </Grid>
          </TableContainer>
        </Body>
      </div>
    );
  } else {
    let text: string = "";
    let showLoadButton: boolean = false;
    switch(state.runData?.eviction.status) {
      case EvictionStatus.Evicted:
        text = "This run's data is not available anymore. Please reload.";
        showLoadButton = true;
        break;
      case EvictionStatus.Loading:
        text = "Loading data from run...";
        break;
      case EvictionStatus.Evicting:
        text = "Evicting data...";
        break;
      case EvictionStatus.Failed:
        text = "Eviction failed. Reason: " + state.runData.eviction.eviction_failure_reason;
        showLoadButton = true;
        break;
    }

    return (
      <div className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5" style={{ overflow: 'visible' }}>
        <TitleWrapper>
          <CardHeader>Label tasks</CardHeader>
        </TitleWrapper>
        {
          text
        }
        {
          showLoadButton && <div>
            <SessionRunEvict runId={state.session?.lastRun?.id || ""} nextState={EvictionStatus.Loading} />
          </div>
        }
      </div>
    );
  }
};

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
`;

const FilterContainer = styled.div`
  min-width: 300px;
  flex-grow: 1;
`;

const TableContainer = styled.div`
  flex-grow: 10;
  margin-left: 32px;
  width: calc(100vw - 550px);
`;

export default SessionLabelOverview;
